import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicLiveAuctionView } from '../../../services/tournamentService';
import AuctionHeroHeader from '../../../components/auction/public/AuctionHeroHeader';
import AuctionSummaryCards from '../../../components/auction/public/AuctionSummaryCards';
import AuctionCurrentPlayerCard from '../../../components/auction/public/AuctionCurrentPlayerCard';
import AuctionPlayersTable from '../../../components/auction/public/AuctionPlayersTable';
import AuctionTeamsGrid from '../../../components/auction/public/AuctionTeamsGrid';
import AuctionOverviewPanels from '../../../components/auction/public/AuctionOverviewPanels';
import { usePublicAuctionView } from '../../../modules/auction-core/hooks/usePublicAuctionView';

const POLL_INTERVAL_MS = 1500;

const PublicLiveAuctionPage = () => {
  const { tournamentId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [playerTab, setPlayerTab] = useState('all');
  const { data, loading, error, isStale, auction, teams, players, soldPlayers, lastUpdatedAt } = usePublicAuctionView({
    tournamentId,
    fetcher: fetchPublicLiveAuctionView,
    pollIntervalMs: POLL_INTERVAL_MS,
    errorMessage: 'Unable to load public live auction view.',
  });
  const currentPlayer = data?.currentPlayer || null;

  const liveStreamingUrl = useMemo(() => [data?.streaming?.watchLiveUrl, auction.liveStreamingUrl, auction.youtubeLiveLink, auction.playbackUrl, data?.liveStreamingUrl].find((url) => typeof url === 'string' && url.trim()) || null, [data?.streaming?.watchLiveUrl, auction.liveStreamingUrl, auction.youtubeLiveLink, auction.playbackUrl, data?.liveStreamingUrl]);
  const replayUrl = data?.streaming?.replay?.url || '';
  const [remainingMs, setRemainingMs] = useState(null);
  useEffect(() => {
    const update = () => {
      const end = data?.live?.currentPlayerEndsAt ? new Date(data.live.currentPlayerEndsAt).getTime() : null;
      setRemainingMs(end ? Math.max(0, end - Date.now()) : null);
    };
    update();
    const timer = setInterval(update, 250);
    return () => clearInterval(timer);
  }, [data?.live?.currentPlayerEndsAt]);

  if (loading) return <div className="container py-4 py-md-5"><div className="card border-0 shadow-sm rounded-4"><div className="card-body p-4">Loading live auction view...</div></div></div>;
  if (error && !data) return <div className="container py-4 py-md-5"><div className="alert alert-danger rounded-4">{error}</div></div>;

  return (
    <div className="container py-4 py-md-5">
      <AuctionHeroHeader auction={auction} lastUpdatedAt={lastUpdatedAt} pollIntervalMs={POLL_INTERVAL_MS} liveStreamingUrl={liveStreamingUrl} />
      {isStale ? <div className="alert alert-warning">Connection interrupted. Showing the last synchronized auction state while reconnecting.</div> : null}
      {data?.live?.completed ? <div className="alert alert-success"><strong>Auction completed.</strong> Final teams and results are shown below.</div> : null}
      {data?.lastOutcome ? <div className={`alert ${data.lastOutcome.result === 'sold' ? 'alert-success' : 'alert-secondary'}`}><strong>{data.lastOutcome.result?.toUpperCase()}</strong> · {data.lastOutcome.player?.playerName || data.lastOutcome.player?.name}{data.lastOutcome.winningTeam ? ` · ${data.lastOutcome.winningTeam.name} · ₹${Number(data.lastOutcome.amount || 0).toLocaleString('en-IN')}` : ''}</div> : null}
      <div className="alert alert-info py-2">Phase: {data?.live?.auctionPhase || 'idle'} · {data?.live?.paused ? 'Paused' : remainingMs === null ? 'Operator timed' : `${Math.ceil(remainingMs / 1000)}s remaining`} · Round {data?.live?.currentRound || 1}</div>
      <AuctionSummaryCards auction={auction} players={players} teams={teams} />
      {(liveStreamingUrl || replayUrl) ? (
        <div className="d-flex gap-2 mb-3">
          {liveStreamingUrl ? <a href={liveStreamingUrl} target="_blank" rel="noreferrer" className="btn btn-danger">Watch live</a> : null}
          {replayUrl ? <a href={replayUrl} target="_blank" rel="noreferrer" className="btn btn-outline-success">Watch replay</a> : null}
        </div>
      ) : null}
      <AuctionCurrentPlayerCard currentPlayer={currentPlayer} />

      <div className="d-flex flex-wrap gap-2 mb-4">{['overview', 'players', 'teams'].map((tab) => <button key={tab} type="button" className={`btn rounded-pill px-4 ${activeTab === tab ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setActiveTab(tab)}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>)}</div>
      {activeTab === 'overview' ? <AuctionOverviewPanels soldPlayers={soldPlayers} teams={teams} /> : null}
      {activeTab === 'players' ? <AuctionPlayersTable players={players} playerTab={playerTab} onTabChange={setPlayerTab} /> : null}
      {activeTab === 'teams' ? <AuctionTeamsGrid teams={teams} /> : null}
    </div>
  );
};

export default PublicLiveAuctionPage;
